import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { z } from "zod";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";

// apiGet / apiSend already unwrap the server's `{ data: ... }` envelope,
// so the schema here describes the inner payload directly. Wrapping it
// in another `{ data: … }` here was the root cause of the "dragged
// column reverts on drop" failure — every save threw on parse and the
// optimistic update got rolled back.
const PreferencesPayloadSchema = z.object({
  hiddenColumns: z.array(z.string()),
  columnOrder: z.array(z.string()),
});

export type TablePreferences = z.infer<typeof PreferencesPayloadSchema>;

const tablePrefsKeys = {
  one: (tableId: string) => ["user-table-preferences", tableId] as const,
};

// Per-user column preferences for a named table surface (`claims-mrp`,
// `policy-mrp`, etc.). The hook composes the user's stored preferences
// with the page's static column registry so callers get a single ordered,
// filtered column list to render — no manual reconciliation.
//
// Save semantics: every change to hidden columns or column order writes
// through to the server immediately. The mutation is fire-and-forget from
// the caller's perspective; we log failures but don't surface them to the
// user (a missed save the next time they reload reverts a single toggle —
// the cost of failing silently here is small and the noisy alternative
// would be worse than the bug).
export function useTablePreferences<K extends string>(args: {
  tableId: string;
  // All column keys this table can render, in their default order. The
  // hook will surface any column not in the user's `columnOrder` at the
  // tail of `orderedKeys`, in this default order — so adding a new column
  // to a page doesn't require touching saved preferences.
  defaultOrder: readonly K[];
  // Columns that should never appear in the manager / never be hidden.
  // Typical use: the sticky-left identifying column (Claim Number, Policy
  // Number) — hiding it would orphan the entire row.
  alwaysVisible?: readonly K[];
}): {
  orderedKeys: K[];
  visibleKeys: K[];
  isHidden: (key: K) => boolean;
  setHidden: (key: K, hidden: boolean) => void;
  setOrder: (next: K[]) => void;
  resetToDefaults: () => void;
  isLoaded: boolean;
} {
  const { tableId, defaultOrder, alwaysVisible } = args;
  const queryClient = useQueryClient();
  const alwaysSet = useMemo(() => new Set<string>(alwaysVisible ?? []), [alwaysVisible]);

  const { data, isLoading } = useQuery<TablePreferences>({
    queryKey: tablePrefsKeys.one(tableId),
    queryFn: () => apiGet(`/api/user-table-preferences/${tableId}`, PreferencesPayloadSchema),
    // Long stale time — column prefs change rarely. Most renders read from
    // cache, mutation writes invalidate.
    staleTime: 5 * 60 * 1000,
  });

  const prefs: TablePreferences = data ?? { hiddenColumns: [], columnOrder: [] };

  const mutation = useMutation({
    mutationFn: (next: TablePreferences) =>
      apiSend("PUT", `/api/user-table-preferences/${tableId}`, next, PreferencesPayloadSchema),
    onMutate: async (next) => {
      // Optimistic write so the table re-renders in the new layout before
      // the network roundtrip lands.
      await queryClient.cancelQueries({ queryKey: tablePrefsKeys.one(tableId) });
      const previous = queryClient.getQueryData<TablePreferences>(tablePrefsKeys.one(tableId));
      queryClient.setQueryData<TablePreferences>(tablePrefsKeys.one(tableId), next);
      return { previous };
    },
    onError: (error, _next, ctx) => {
      logger.error("user_table_preferences_save_failed", { error, tableId });
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(tablePrefsKeys.one(tableId), ctx.previous);
      }
    },
  });

  // Compose stored order + defaults: known keys in user order first, then
  // any default keys the user hasn't placed yet. Defensive against stored
  // orders that reference removed columns (skip those).
  const orderedKeys = useMemo<K[]>(() => {
    const defaults = defaultOrder as readonly string[];
    const defaultSet = new Set<string>(defaults);
    const seen = new Set<string>();
    const result: string[] = [];
    for (const key of prefs.columnOrder) {
      if (defaultSet.has(key) && !seen.has(key)) {
        result.push(key);
        seen.add(key);
      }
    }
    for (const key of defaults) {
      if (!seen.has(key)) {
        result.push(key);
        seen.add(key);
      }
    }
    return result as K[];
  }, [prefs.columnOrder, defaultOrder]);

  const hiddenSet = useMemo(() => {
    // Always-visible keys can never be hidden, even if stored that way.
    return new Set<string>(prefs.hiddenColumns.filter((k) => !alwaysSet.has(k)));
  }, [prefs.hiddenColumns, alwaysSet]);

  const visibleKeys = useMemo<K[]>(
    () => orderedKeys.filter((k) => !hiddenSet.has(k)),
    [orderedKeys, hiddenSet],
  );

  function persist(nextHidden: string[], nextOrder: string[]) {
    mutation.mutate({ hiddenColumns: nextHidden, columnOrder: nextOrder });
  }

  function setHidden(key: K, hidden: boolean) {
    if (alwaysSet.has(key)) return; // guarded — can't be hidden
    const next = new Set(hiddenSet);
    if (hidden) next.add(key);
    else next.delete(key);
    persist(Array.from(next), orderedKeys);
  }

  function setOrder(next: K[]) {
    persist(Array.from(hiddenSet), next);
  }

  function resetToDefaults() {
    persist([], []);
  }

  return {
    orderedKeys,
    visibleKeys,
    isHidden: (key) => hiddenSet.has(key),
    setHidden,
    setOrder,
    resetToDefaults,
    isLoaded: !isLoading,
  };
}
